
<template>
  <div class="main-content">
    <Container
      :title="t('route.materialAttributeValue')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          ref="searchFormRef"
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="submitSearch"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-button
          v-if="this.$hasPermission(['ACTION_vcMaterialAttributeValueEdit'])"
          @click="handleCreate()"
          type="success"
        >
          {{ t('table.add') }}
        </el-button>
        <el-button
          v-if="selection.length > 0 && this.$hasPermission(['ACTION_vcMaterialAttributeValueEdit'])"
          @click="handleBathDelete()"
          type="danger"
        >
          {{ t('table.bathDelete') }}
        </el-button>
        <SimpleTable
          :isLoading="listLoading"
          :tableData="tableData"
          :columnStruct="columnStruct"
          :sort="sort"
          :order="order"
          @handleOrder="handleOrder"
          @handleSelection="handleSelection"
        >
          <template
            v-if="this.$hasPermission(['ACTION_vcMaterialAttributeValueEdit'])"
            #default="slotProps"
          >
            <el-button
              @click="handleEdit(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('common.edit') }}
            </el-button>
            <el-button
              @click="handleDelete(slotProps.row.id)"
              type="danger"
              size="mini"
            >
              {{ t('common.delete') }}
            </el-button>
          </template>
        </SimpleTable>
        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[5, 10, 20]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>
    <el-dialog
      v-if="this.$hasPermission(['ACTION_vcMaterialAttributeValueEdit'])"
      :title="t(textMap[dialogStatus])"
      center
      v-model="dialogFormVisible"
      width="45%"
    >
      <NomalContainer>
        <CenterForm
          v-if="dialogStatus === 'create'"
          :formModel="createModel"
          :formStruct="createStruct"
          :formRules="materialAttributeValueRole"
          @submit="createSubmit"
          @reset="createReset"
        />
        <CenterForm
          v-if="dialogStatus === 'update'"
          :formModel="updateModel"
          :formStruct="updateStruct"
          :formRules="materialAttributeValueRole"
          @submit="updateNameSubmit"
          @reset="updateNameReset"
        />
      </NomalContainer>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  MaterialAttributeValueModel,
  MaterialAttributeValuePageSearchModel,
  MaterialAttributeValueSearchModel,
  MaterialAttributeValueAddModel,
  MaterialAttributeValueUpdateModel,
  IdsModel
} from '@/model/product/MaterialAttributeValueModel'
import {
  pageSearchMaterialAttributeValue,
  addMaterialAttributeValue,
  updateMaterialAttributeValue,
  removeMaterialAttributeValue,
  batchRemoveMaterialAttributeValue
} from '@/apis/product/materialAttributeValue'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const searchStruct = [
      { prop: 'materialId', label: t('materialAttributeValue.materialId'), placeholder: t('materialAttributeValue.materialIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'attributeId', label: t('materialAttributeValue.attributeId'), placeholder: t('materialAttributeValue.attributeIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'value', label: t('materialAttributeValue.value'), placeholder: t('materialAttributeValue.valueP'), component: 'el-input', type: 'text', model: null }
    ]

    const createStruct = [
      { prop: 'materialId', label: t('materialAttributeValue.materialId'), placeholder: t('materialAttributeValue.materialIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'attributeId', label: t('materialAttributeValue.attributeId'), placeholder: t('materialAttributeValue.attributeIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'value', label: t('materialAttributeValue.value'), placeholder: t('materialAttributeValue.valueP'), component: 'el-input', type: 'text', model: null }
    ]

    const updateStruct = [
      { prop: 'materialId', label: t('materialAttributeValue.materialId'), placeholder: t('materialAttributeValue.materialIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'attributeId', label: t('materialAttributeValue.attributeId'), placeholder: t('materialAttributeValue.attributeIdP'), component: 'el-input', type: 'number', model: null },
      { prop: 'value', label: t('materialAttributeValue.value'), placeholder: t('materialAttributeValue.valueP'), component: 'el-input', type: 'text', model: null }
    ]

    const materialAttributeValueRole = {
      materialId: [{ required: true, message: t('materialAttributeValue.materialId').concat(t('common.isRequired')), trigger: 'blur' }],
      attributeId: [{ required: true, message: t('materialAttributeValue.attributeId').concat(t('common.isRequired')), trigger: 'blur' }],
      value: [{ required: true, message: t('materialAttributeValue.value').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [

      { prop: 'materialId', label: t('materialAttributeValue.materialId'), sortable: 'custom' },
      { prop: 'attributeId', label: t('materialAttributeValue.attributeId'), sortable: 'custom' },
      { prop: 'value', label: t('materialAttributeValue.value'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: MaterialAttributeValueModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: MaterialAttributeValueModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as MaterialAttributeValueSearchModel,
      tableData: Array<MaterialAttributeValueModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'materialAttributeValue.edit',
        create: 'materialAttributeValue.create'
      },
      dialogFormVisible: false,
      materialAttributeValueRole: materialAttributeValueRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as MaterialAttributeValueAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as MaterialAttributeValueUpdateModel,
      editRow: {} as MaterialAttributeValueModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: MaterialAttributeValuePageSearchModel = {
          materialId: dataMap.searchFormModel.materialId,
          attributeId: dataMap.searchFormModel.attributeId,
          value: dataMap.searchFormModel.value,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchMaterialAttributeValue(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async createSubmit() {
        const createData: MaterialAttributeValueAddModel = {
          materialId: dataMap.createModel.materialId,
          attributeId: dataMap.createModel.attributeId,
          value: dataMap.createModel.value
        }
        const result = await addMaterialAttributeValue(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: MaterialAttributeValueUpdateModel = {
          id: dataMap.updateModel.id,
          materialId: dataMap.updateModel.materialId,
          attributeId: dataMap.updateModel.attributeId,
          value: dataMap.updateModel.value
        }
        const result = await updateMaterialAttributeValue(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeMaterialAttributeValue(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveMaterialAttributeValue(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.materialId = dataMap.editRow.materialId
        dataMap.updateModel.attributeId = dataMap.editRow.attributeId
        dataMap.updateModel.value = dataMap.editRow.value
      },
      createReset() {
        console.log('create reset')
      }
    })
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: MaterialAttributeValueModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.materialId = row.materialId
        dataMap.updateModel.attributeId = row.attributeId
        dataMap.updateModel.value = row.value
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      ...toRefs(dataMap)
    }
  }
})
</script>
